import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './SoundDesign.module.css';
import ReactPlayer from 'react-player';
import { v4 as uuidv4 } from 'uuid';
import { IoCloseOutline } from "react-icons/io5";
import { trackVideoInteraction, trackExternalLink, trackNavigation, trackButtonClick, trackModal } from '../utils/analytics';

const videos = [
  {
    id: 2,
    title: 'Agnes Addington',
    website: 'https://bencleek.wixsite.com/benjamincleek/agnes-addington', 
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/7206a2d5-8a91-4eca-af59-83d91a757710-Agnes%20Addington.jpg',
  },
  {
    id: 13,
    title: 'Alma',
    pageLink: '/alma',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/8c352e07-0ee8-46b4-ab1b-6296a72d539f-Screen%20Shot%202024-08-03%20at%202.05.03%20PM.png',
  },
  {
    id: 12,
    title: 'Nocturne',
    videoUrl: 'https://vimeo.com/949317678',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/9faa68cf-ffce-49b5-bb78-ef23955e0d97-Nocturne.jpg',
  },
  {
    id: 3,
    title: 'John Sunshine JSLRNRT',
    videoUrl: 'https://vimeo.com/686909888',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/b0691f10-c810-458f-8e87-4ae5042dd9ef-John%20Sunshine%20JSLRNRT%20poster.png',
  },
  // {
  //   id: 11,
  //   title: 'Sliding Scale',
  //   videoUrl: 'https://www.youtube.com/watch?v=-YEDn6mSKJg', 
  //   thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/8b168917-1d1f-49c4-b9e9-e57b937c1f24-Sliding%20Scale%20thumb.jpg',
  // },
  {
    id: 1,
    title: 'Hello Goodbye',
    videoUrl: 'https://youtu.be/mwMSRdp6Fwc',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/9dd569eb-3340-41c2-b092-b40c0764f532-Hello_Goodbye-smaller.jpg',
  },
  {
    id: 14,
    title: 'Lumberjack',
    pageLink: '/lumberjack',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/8ce386c5-29bd-4ab5-86c7-f372368dbce2-lumberjack%20poster.jpg',
  },
  // {
  //   id: 5,
  //   title: 'Slowly Quickly',
  //   videoUrl: 'https://vimeo.com/11598388',
  //   thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/0a323ad9-b610-4377-a693-d1ce5c707c65-Slowly%20Quickly%20%281%29.jpg',
  // },
];

const VideoCard = ({ video, onClick }) => (
  <div
    className={styles.videoCard}
    onClick={(event) => onClick(video, event)}
  >
    <div className={styles.imageWrapper}>
      <img className={styles.videoImg} src={video.thumbnail} alt={video.title} />
    </div>
    <div className={styles.cardInfo}>
      <p className={styles.cardTitle}>{video.title}</p>
    </div>
  </div>
);

const SoundDesign = () => {
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlayerVisible, setPlayerVisible] = useState(false);
  const playerRef = useRef(null);
  const enteredFsRef = useRef(false);

  const requestMobileFullscreen = () => {
    if (!window.matchMedia?.('(pointer: coarse)').matches) return;
    const el = document.documentElement;
    const fsReq = el.requestFullscreen ?? el.webkitRequestFullscreen;
    if (fsReq) {
      enteredFsRef.current = true;
      fsReq.call(el).then(() => screen.orientation?.unlock?.()).catch(() => {
        enteredFsRef.current = false;
      });
    }
  };

  const exitMobileFullscreen = () => {
    if (!enteredFsRef.current) return;
    enteredFsRef.current = false;
    (document.exitFullscreen ?? document.webkitExitFullscreen)?.call(document);
  };

  const handleVideoClick = (video, event) => {
    if (video.website) {
      trackExternalLink(video.website, video.title);
      window.open(video.website, '_blank');
      return;
    }

    if (video.pageLink) {
      trackNavigation(video.title, video.pageLink);
      navigate(video.pageLink);
      return;
    }

    if (selectedVideo && selectedVideo.id === video.id) {
      trackVideoInteraction('close', video.title, 'SoundDesign');
      exitMobileFullscreen();
      setSelectedVideo(null);
      setPlayerVisible(false);
    } else {
      trackVideoInteraction('play', video.title, 'SoundDesign');
      trackModal('open', `SoundDesign - ${video.title}`);
      setSelectedVideo(video);
      setPlayerVisible(true);
      requestMobileFullscreen();
    }

    event.stopPropagation();
  };

  const handleCloseModal = () => {
    if (selectedVideo) {
      trackVideoInteraction('close', selectedVideo.title, 'SoundDesign');
      trackModal('close', `SoundDesign - ${selectedVideo.title}`);
    }
    exitMobileFullscreen();
    setPlayerVisible(false);
    setSelectedVideo(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        playerRef.current &&
        !playerRef.current.contains(event.target) &&
        !event.target.closest(`.${styles.modalContent}`)
      ) {
        handleCloseModal();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      if (!(document.fullscreenElement ?? document.webkitFullscreenElement) && enteredFsRef.current) {
        enteredFsRef.current = false;
        setPlayerVisible(false);
        setSelectedVideo(null);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  return (
    <div className={styles.soundDesign}>
      <div className={styles.videoThumbs}>
        {videos.map((video) => (
          <VideoCard key={video.id} video={video} onClick={handleVideoClick} />
        ))}
        {selectedVideo && isPlayerVisible && (
          <div className={styles.modal}>
            <div className={styles.modalContent} ref={playerRef}>
              {selectedVideo.videoUrl && (
                <ReactPlayer
                  url={selectedVideo.videoUrl}
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  controls
                  playing
                />
              )}
              <button 
                onClick={handleCloseModal} 
                className={styles.button}
                onMouseDown={(e) => {
                  trackButtonClick('close_modal', 'SoundDesign', { video_title: selectedVideo?.title });
                }}
              >
                <IoCloseOutline />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoundDesign;
